import { isPlatformStaffRole, normalizePlatformRole } from '@/utils/teamRoles';
import { titleCaseLabel } from '@/utils/labelFormat';

/** Assignable workspace roles in customer organizations. */
export const CUSTOMER_WORKSPACE_ROLE_OPTIONS = [
  { value: 'workspace_admin', label: 'Workspace Admin' },
  { value: 'subscriber', label: 'Subscriber' },
  { value: 'viewer', label: 'Viewer' },
] as const;

/** @deprecated use CUSTOMER_WORKSPACE_ROLE_OPTIONS */
export const WORKSPACE_ROLE_OPTIONS = CUSTOMER_WORKSPACE_ROLE_OPTIONS;

const WORKSPACE_ROLE_LABELS: Record<string, string> = {
  workspace_admin: 'Workspace Admin',
  organization_owner: 'Workspace Admin',
  organization_admin: 'Workspace Admin',
  subscriber: 'Subscriber',
  member: 'Subscriber',
  editor: 'Subscriber',
  viewer: 'Viewer',
  internal_staff: 'Staff',
  staff: 'Staff',
};

export function platformStaffBadgeLabel(platformRole?: string | null): string {
  const role = normalizePlatformRole(platformRole);
  if (role === 'none') return 'Customer';
  if (role === 'platform_support') return 'Platform Support';
  if (role === 'platform_operator') return 'Platform Support';
  if (role === 'owner') return 'Owner';
  if (role === 'platform_admin') return 'Platform Admin';
  return titleCaseLabel(role);
}

/** Human-readable platform account role (global permissions). */
export function platformRoleLabel(platformRole?: string | null): string {
  if (!platformRole) return 'None';
  const role = normalizePlatformRole(platformRole);
  if (role === 'none') return 'None';
  if (isPlatformStaffRole(role)) {
    return platformStaffBadgeLabel(role);
  }
  return titleCaseLabel(role);
}

export function workspaceRoleLabel(
  role: string,
  options?: {
    platformRole?: string | null;
    membershipRoleLabel?: string | null;
  },
): string {
  if (options?.membershipRoleLabel) {
    return options.membershipRoleLabel;
  }
  const key = role.trim().toLowerCase();
  return WORKSPACE_ROLE_LABELS[key] ?? key.replace(/_/g, ' ');
}

export function canEditWorkspaceMembershipRole(
  member: { membershipRole: string; platformRole?: string | null },
): boolean {
  const membershipRole = member.membershipRole.trim().toLowerCase();
  if (membershipRole === 'organization_owner') return false;
  if (member.platformRole && isPlatformStaffRole(member.platformRole)) {
    return false;
  }
  return true;
}

export function formatOrganizationNames(names?: string[] | null): string {
  if (!names || names.length === 0) return '—';
  return names.join(', ');
}

export function normalizeWorkspaceRole(role?: string | null): string {
  const value = (role ?? 'subscriber').trim().toLowerCase();
  if (value === 'organization_admin' || value === 'organization_owner' || value === 'admin' || value === 'customer_admin' || value === 'member' || value === 'editor') {
    if (value === 'member' || value === 'editor') return 'subscriber';
    return 'workspace_admin';
  }
  return CUSTOMER_WORKSPACE_ROLE_OPTIONS.some((option) => option.value === value) ? value : 'subscriber';
}
